import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useFormik, FormikHelpers } from 'formik';
import { object, string } from 'yup';

import { postSubscribe } from '@/api';
import { submitHandler } from '@/utils/forms';

import Button from '@/components/UI/Button';
import TextField from '@/components/UI/InputText';
import SROnly from '@/components/UI/SROnly';

interface SubscribeFormProps {
  buttonLabel: string;
  fieldLabel: string;
  fieldPlaceholder: string;
  lang: string;
}

interface SubscribeFormValues {
  subscribe: string;
}

const validationSchema = object().shape({
  subscribe: string().email().required()
});

const initialValues: SubscribeFormValues = {
  subscribe: ''
};

const SubscribeForm = ({
  buttonLabel,
  fieldLabel,
  fieldPlaceholder,
  lang
}: SubscribeFormProps) => {
  const mountedRef = useRef(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const onHintClick = useCallback(() => setSuccessMessage(''), []);

  const onSuccess = useCallback((
    _values: SubscribeFormValues,
    _helpers: FormikHelpers<SubscribeFormValues>,
    result: unknown
  ) => {
    if (typeof gtag === 'function') {
      gtag('event', 'subscription', { event_category: 'newsletter' });
    }

    setErrorMessage('');
    setSuccessMessage((result as {
      data?: {
        message?: string
      }
    })?.data?.message || '');

    setTimeout(() => {
      if (mountedRef.current) {
        setSuccessMessage('');
      }
    }, 10000);
  }, []);

  const onError = useCallback((
    _values: SubscribeFormValues,
    _helpers: FormikHelpers<SubscribeFormValues>,
    result: unknown
  ) => {
    setErrorMessage((result as {
      data?: {
        message?: string
      }
    })?.data?.message || '');
  }, []);

  const {
    errors,
    isSubmitting,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit
  } = useFormik<SubscribeFormValues>({
    initialValues,
    validationSchema,
    onSubmit: (form, helper) => {
      const onSend = () => postSubscribe({ email: form.subscribe }, lang);
      return submitHandler(form, helper, onSend, onSuccess, onError);
    }
  });

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <form
      className="subscribe-form"
      noValidate
      onSubmit={handleSubmit}
    >
      <Button
        className={cn('subscribe-form-hint', {
          error: !!errorMessage,
          opened: errorMessage || successMessage
        })}
        type="button"
        onClick={onHintClick}
      >
        {errorMessage || successMessage}
      </Button>

      <SROnly
        component="label"
        htmlFor="subscribe"
      >
        {buttonLabel}
      </SROnly>

      <TextField
        autoComplete="off"
        aria-label={fieldLabel}
        className={cn({ error: touched.subscribe && errors.subscribe })}
        id="subscribe"
        name="subscribe"
        placeholder={fieldPlaceholder}
        value={values.subscribe}
        type="email"
        onBlur={handleBlur}
        onChange={handleChange}
      />

      <Button
        className="subscribe-form-submit"
        disabled={isSubmitting}
        type="submit"
      >
        <SROnly>
          {buttonLabel}
        </SROnly>
      </Button>
    </form>
  );
};

export default memo(SubscribeForm);
